import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, RefreshCw, Filter, RotateCw } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import AdminShell from "@/components/AdminShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUSES = ["all", "queued", "processing", "completed", "failed"];

function statusChip(s) {
    if (s === "completed") return "ddi-chip ddi-chip-success";
    if (s === "failed") return "ddi-chip ddi-chip-error";
    if (s === "processing") return "ddi-chip ddi-chip-warning";
    return "ddi-chip ddi-chip-primary";
}

function AiJobRow({ j, expanded, onToggleError, onRetry, retrying }) {
    return (
        <>
            <tr className="border-t border-[#EEF1F6]" data-testid={`ai-job-${j.id}`}>
                <td className="px-4 py-3">
                    <div className="text-[#0F172A] font-medium">{j.doctor?.full_name || <span className="text-[#94A3B8]">—</span>}</div>
                    <div className="text-[11px] text-[#94A3B8]">{j.doctor?.specialization}</div>
                </td>
                <td className="px-4 py-3 text-[11px] text-[#64748B] font-mono">{j.source || "workflow"}</td>
                <td className="px-4 py-3 text-[11px] text-[#64748B]">{j.model || "-"}<br /><span className="text-[10px] text-[#94A3B8]">{j.prompt_version || ""}</span></td>
                <td className="px-4 py-3"><span className={statusChip(j.status)}>{j.status}</span></td>
                <td className="px-4 py-3 text-[#64748B]">{j.retry_count || 0}</td>
                <td className="px-4 py-3 text-[11px] text-[#64748B]">{j.created_at ? new Date(j.created_at).toLocaleString() : "-"}</td>
                <td className="px-4 py-3 text-right">
                    {j.status === "failed" && (
                        <button
                            onClick={onRetry}
                            disabled={retrying}
                            className="text-[12px] text-[#3A80F7] font-semibold hover:underline inline-flex items-center gap-1"
                            data-testid={`ai-job-retry-${j.id}`}
                        >
                            <RotateCw size={11} className={retrying ? "animate-spin" : ""} />
                            Retry
                        </button>
                    )}
                    {j.error && (
                        <button
                            onClick={onToggleError}
                            className="text-[12px] text-[#64748B] hover:text-[#DC2626] ml-3"
                            data-testid={`ai-job-error-toggle-${j.id}`}
                        >
                            Error
                        </button>
                    )}
                </td>
            </tr>
            {expanded && j.error && (
                <tr className="border-t border-[#EEF1F6] bg-[#FEE2E2]/30">
                    <td colSpan={7} className="px-4 py-3 text-[12px] text-[#7F1D1D] font-mono whitespace-pre-wrap">{j.error}</td>
                </tr>
            )}
        </>
    );
}

export default function AdminAIJobs() {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [search] = useSearchParams();
    const [status, setStatus] = useState(search.get("status") || "all");
    const [retrying, setRetrying] = useState(null);
    const [expanded, setExpanded] = useState(null);
    const [loading, setLoading] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const q = new URLSearchParams({ page: String(page), page_size: "25", days: "30" });
            if (status !== "all") q.set("status", status);
            const r = await api.get(`/admin/ai-jobs?${q.toString()}`);
            setRows(r.data.items || []);
            setTotal(r.data.total || 0);
        } catch (e) {
            toast.error(errMsg(e, "Failed"));
        } finally { setLoading(false); }
    }
    useEffect(() => { load(); }, [status, page]);

    async function retry(id) {
        setRetrying(id);
        try {
            await api.post(`/admin/ai-jobs/${id}/retry`);
            toast.success("Job requeued");
            await load();
        } catch (e) {
            toast.error(errMsg(e, "Retry failed"));
        } finally { setRetrying(null); }
    }

    return (
        <AdminShell>
            <div className="mb-6 flex items-center justify-between" data-testid="admin-ai-header">
                <div>
                    <h1 className="text-3xl font-bold text-[#0F172A] tracking-tight">AI Jobs</h1>
                    <p className="text-[#64748B] mt-1 text-[14px]">Monitor content generation. Retry failed jobs.</p>
                </div>
                <button onClick={load} disabled={loading} className="ddi-btn-outline" data-testid="admin-ai-refresh">
                    <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
                </button>
            </div>

            <Card className="border-[#E5E9F2] mb-5 ddi-shadow-sm">
                <CardContent className="py-4 flex items-center gap-3">
                    <Filter size={14} className="text-[#64748B]" />
                    <span className="text-[12px] text-[#64748B]">Status</span>
                    <Select value={status} onValueChange={(v) => { setPage(1); setStatus(v); }}>
                        <SelectTrigger className="w-40" data-testid="admin-ai-filter-status"><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                    <div className="ml-auto text-[12px] text-[#64748B]">{total} jobs</div>
                </CardContent>
            </Card>

            <Card className="border-[#E5E9F2] ddi-shadow-sm overflow-hidden">
                {rows.length === 0 ? (
                    <div className="py-16 text-center">
                        <Sparkles size={40} className="mx-auto text-[#94A3B8] mb-3" />
                        <div className="text-[13px] text-[#64748B]">No AI jobs in this filter.</div>
                    </div>
                ) : (
                    <table className="w-full text-[13px]">
                        <thead className="bg-[#F5F7FB] text-[#334155]">
                            <tr>
                                <th className="text-left px-4 py-3 font-semibold">Doctor</th>
                                <th className="text-left px-4 py-3 font-semibold">Source</th>
                                <th className="text-left px-4 py-3 font-semibold">Model</th>
                                <th className="text-left px-4 py-3 font-semibold">Status</th>
                                <th className="text-left px-4 py-3 font-semibold">Retries</th>
                                <th className="text-left px-4 py-3 font-semibold">Created</th>
                                <th className="text-right px-4 py-3 font-semibold">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((j) => (
                                <AiJobRow
                                    key={j.id}
                                    j={j}
                                    expanded={expanded === j.id}
                                    onToggleError={() => setExpanded(expanded === j.id ? null : j.id)}
                                    onRetry={() => retry(j.id)}
                                    retrying={retrying === j.id}
                                />
                            ))}
                        </tbody>
                    </table>
                )}
            </Card>

            {total > 25 && (
                <div className="mt-4 flex items-center justify-center gap-2">
                    <Button variant="outline" onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1}>Previous</Button>
                    <span className="text-[12px] text-[#64748B]">Page {page} of {Math.ceil(total / 25)}</span>
                    <Button variant="outline" onClick={() => setPage(page + 1)} disabled={page * 25 >= total}>Next</Button>
                </div>
            )}
        </AdminShell>
    );
}
